import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { EntityList, type EntityListColumn } from '@/components/crm/entity-list'
import { KanbanBoard, type KanbanColumn } from '@/components/crm/kanban-board'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/crm/opportunities/')({
  component: OpportunitiesIndexPage,
})

type OpportunityRow = {
  id: string
  name?: string | null
  accountId?: string | null
  amount?: string | null
  currency?: string | null
  pipelineId?: string | null
  stageId?: string | null
  probability?: number | null
  expectedCloseDate?: string | Date | null
  ownerUserId?: string | null
  createdAt?: string | Date | null
  stageChangedAt?: string | Date | null
}

type PipelineRow = { id: string; name: string; isDefault: boolean }
type StageRow = { id: string; name: string; pipelineId: string; sortOrder: number }
type AccountRow = { id: string; name?: string | null }

function formatDate(v: string | Date | null | undefined): string {
  if (!v) return '-'
  try {
    return new Date(v).toLocaleDateString()
  } catch {
    return '-'
  }
}

function stageAgeDays(v: string | Date | null | undefined): string {
  if (!v) return ''
  try {
    const ms = Date.now() - new Date(v).getTime()
    const days = Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)))
    return `${days}d`
  } catch {
    return ''
  }
}

function OpportunitiesIndexPage() {
  const t = useTranslations()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [view, setView] = useState<'kanban' | 'list'>('kanban')

  const { data: pipelineData } = useQuery(orpc.crm.pipelines.list.queryOptions({}))
  const pipelines = (pipelineData as PipelineRow[] | undefined) ?? []
  const defaultPipelineId = useMemo(
    () => pipelines.find((p) => p.isDefault)?.id ?? pipelines[0]?.id ?? '',
    [pipelines],
  )
  const [pipelineId, setPipelineId] = useState<string>('')
  useEffect(() => {
    if (!pipelineId && defaultPipelineId) setPipelineId(defaultPipelineId)
  }, [pipelineId, defaultPipelineId])

  const { data: stageData } = useQuery(
    orpc.crm.stages.list.queryOptions({
      input: { pipelineId: pipelineId || '00000000-0000-0000-0000-000000000000' },
      enabled: !!pipelineId,
    }),
  )
  const stages = (stageData as StageRow[] | undefined) ?? []

  const { data: oppData, isLoading } = useQuery(
    orpc.crm.opportunities.list.queryOptions({
      input: { limit: 500, offset: 0, pipelineId: pipelineId || undefined },
      enabled: !!pipelineId,
    }),
  )
  const opportunities = (oppData as OpportunityRow[] | undefined) ?? []

  const { data: accountData } = useQuery(
    orpc.crm.accounts.list.queryOptions({ input: { limit: 500, offset: 0 } }),
  )
  const accountMap = useMemo(() => {
    const m = new Map<string, string>()
    for (const a of (accountData as AccountRow[] | undefined) ?? []) {
      if (a.name) m.set(a.id, a.name)
    }
    return m
  }, [accountData])

  const stageMap = useMemo(() => {
    const m = new Map<string, string>()
    for (const s of stages) m.set(s.id, s.name)
    return m
  }, [stages])

  const changeStageMutation = useMutation(
    orpc.crm.opportunities.changeStage.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.crm.opportunities.list.key() })
      },
      onError: (e) => toast.error(e.message),
    }),
  )

  const kanbanColumns: KanbanColumn<OpportunityRow>[] = useMemo(
    () =>
      stages.map((stage) => {
        const colRows = opportunities.filter((o) => o.stageId === stage.id)
        return {
          key: stage.id,
          title: stage.name,
          subtitle: `${colRows.length} ${t('crm.opportunity.entityPlural').toLowerCase()}`,
          rows: colRows,
        }
      }),
    [stages, opportunities],
  )

  const handleMoveCard = (recordId: string, _from: string, to: string) => {
    changeStageMutation.mutate({ id: recordId, stageId: to })
  }

  const listColumns: EntityListColumn<OpportunityRow>[] = [
    {
      key: 'name',
      header: t('crm.opportunity.name'),
      render: (r) => <span className="font-medium">{r.name ?? '(unnamed)'}</span>,
    },
    {
      key: 'account',
      header: t('crm.opportunity.account'),
      render: (r) => (r.accountId ? (accountMap.get(r.accountId) ?? '-') : '-'),
    },
    {
      key: 'stage',
      header: t('crm.opportunity.stage'),
      render: (r) => (r.stageId ? (stageMap.get(r.stageId) ?? '-') : '-'),
    },
    {
      key: 'amount',
      header: t('crm.opportunity.amount'),
      render: (r) => (r.amount ? `${r.amount} ${r.currency ?? ''}`.trim() : '-'),
    },
    {
      key: 'probability',
      header: t('crm.opportunity.probability'),
      render: (r) => (typeof r.probability === 'number' ? `${r.probability}%` : '-'),
    },
    {
      key: 'expectedCloseDate',
      header: t('crm.opportunity.expectedCloseDate'),
      render: (r) => formatDate(r.expectedCloseDate),
    },
    { key: 'owner', header: t('crm.common.ownedBy'), render: (r) => r.ownerUserId ?? '-' },
  ]

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('crm.opportunity.entityPlural')}</h1>
        <div className="flex items-center gap-2">
          <Select value={pipelineId} onValueChange={setPipelineId}>
            <SelectTrigger className="w-56">
              <SelectValue placeholder={t('crm.opportunity.pipeline')} />
            </SelectTrigger>
            <SelectContent>
              {pipelines.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant={view === 'kanban' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('kanban')}
          >
            {t('crm.opportunity.viewKanban')}
          </Button>
          <Button
            variant={view === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('list')}
          >
            {t('crm.opportunity.viewList')}
          </Button>
          <Button size="sm" onClick={() => navigate({ to: '/crm/opportunities/new' })}>
            {t('crm.opportunity.newOpportunity')}
          </Button>
        </div>
      </div>

      {view === 'kanban' ? (
        <KanbanBoard
          columns={kanbanColumns}
          renderCard={(r) => (
            <div className="space-y-1">
              <div className="font-medium truncate">{r.name ?? '(unnamed)'}</div>
              {r.accountId && (
                <div className="text-xs text-muted-foreground truncate">
                  {accountMap.get(r.accountId) ?? ''}
                </div>
              )}
              <div className="flex items-center justify-between text-xs">
                <span>{r.amount ? `${r.amount} ${r.currency ?? ''}`.trim() : ''}</span>
                <span className="text-muted-foreground">
                  {r.ownerUserId ? r.ownerUserId.slice(0, 2).toUpperCase() : ''}
                </span>
              </div>
              {r.stageChangedAt && (
                <div className="text-[10px] text-muted-foreground">
                  {t('crm.opportunity.stage')} {stageAgeDays(r.stageChangedAt)}
                </div>
              )}
            </div>
          )}
          onMoveCard={handleMoveCard}
          onCardClick={(r) =>
            navigate({
              to: '/crm/opportunities/$opportunityId',
              params: { opportunityId: r.id },
            })
          }
        />
      ) : (
        <EntityList
          rows={opportunities}
          columns={listColumns}
          searchAccessor={(r) => `${r.name ?? ''}`}
          searchPlaceholder={`${t('crm.common.search')}…`}
          isLoading={isLoading}
          onRowClick={(r) =>
            navigate({
              to: '/crm/opportunities/$opportunityId',
              params: { opportunityId: r.id },
            })
          }
        />
      )}
    </div>
  )
}
